import { useForm } from '@tanstack/react-form';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { log } from 'evlog/client';
import { Home } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate, useParams } from 'react-router';
import { z } from 'zod';

import {
  useEarliestBookableDate,
  useFetchDateForMonth,
  useMonthlySlots,
} from '@virtual-care/hooks';
import { displayName, toAge } from '@virtual-care/isomorphic';
import {
  AttachFilesField,
  uploadFile,
  useUploadProgress,
} from '@virtual-care/media-web';
import {
  Button,
  Calendar,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
  cn,
} from '@virtual-care/shadcn';
import {
  BirthDateField,
  FieldMessage,
  FormMessage,
  GenderField,
  Loading,
  TimeSlots,
  type UiTimeSlot,
  errorToFormIssue,
  useAction,
  useData,
} from '@virtual-care/ui';

import { requiresAuth } from '../../auth-loader.ts';
import Header from '../../components/header/header.tsx';
import ProfileCard from '../../components/header/profile-card.tsx';
import * as inputs from '../../components/inputs.ts';
import { PrivacyNote, SymptomHints } from '../../components/symptom-form.tsx';
import { homecareReservationStore } from '../../hooks/reservation/homecare.ts';

export default function HomecareBookService() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { vendorId } = useParams();

  const bookingSchema = z.object({
    vendorServiceId: z.string().min(1, {
      message: t('homecare.bookService.validation.serviceRequired'),
    }),
    symptoms: z
      .string()
      .min(10, { message: t('homecare.bookService.validation.symptomsMin') }),
    files: inputs.files,
    patientGender: z.string().optional(),
    patientBirthDate: z.string().optional(),
  });
  const loaderData = useLoaderData() as Awaited<
    ReturnType<typeof homecareBookServiceLoader>
  >;
  const { data: vendor, isLoading } = useData('GET /homecare/vendors/{id}', {
    id: vendorId!,
  });

  const earliestBookableDate = useEarliestBookableDate();
  const [date, setDate] = useState<Date | undefined>(earliestBookableDate);
  const [selectedSlot, setSelectedSlot] = useState<UiTimeSlot | undefined>();
  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );
  const [displayedMonth, setDisplayedMonth] =
    useState<Date>(earliestBookableDate);

  const fetchDate = useFetchDateForMonth(displayedMonth);
  const { data: monthlyData, isFetching } = useData(
    'GET /homecare/availability/vendor/{vendorId}/monthly-slots',
    {
      vendorId: vendorId as string,
      date: fetchDate.toISOString(),
    },
    { enabled: !!vendorId },
  );
  const { disabledDates, selectedDateSlots } = useMonthlySlots(
    monthlyData,
    date,
  );

  const isB2B = !loaderData.identity.b2cProfile;
  const [bookingFor, setBookingFor] = useState<'self' | 'other'>(
    isB2B ? 'other' : 'self',
  );
  const isOnBehalf = bookingFor === 'other';
  const fullName = displayName(loaderData.identity);
  const age = loaderData.identity.b2cProfile
    ? `${toAge(loaderData.identity.b2cProfile)} ${t('booking.symptomChecker.fields.ageSuffix')}`
    : '';
  const rawGender = loaderData.identity.b2cProfile?.gender;
  const gender =
    rawGender === 'male' || rawGender === 'female'
      ? t(`common.${rawGender}`)
      : '';

  const [patientName, setPatientName] = useState('');
  const uploads = useUploadProgress();

  const form = useForm({
    defaultValues: {
      vendorServiceId: '',
      symptoms: import.meta.env.DEV
        ? t('booking.symptomChecker.subtitleSelf')
        : '',
      files: [] as File[],
      patientGender: '',
      patientBirthDate: '',
    } as z.input<typeof bookingSchema>,
    validators: {
      onSubmit: bookingSchema,
      onSubmitAsync: async ({ value }) => {
        log.info({
          action: 'homecare_book',
          vendorId,
          serviceId: value.vendorServiceId,
          fileCount: value.files?.length ?? 0,
        });
        if (!selectedSlot || !vendor) {
          return errorToFormIssue(
            new Error(t('homecare.bookService.validation.slotRequired')),
          );
        }

        if (
          isOnBehalf &&
          (!patientName || !value.patientBirthDate || !value.patientGender)
        ) {
          return errorToFormIssue(
            new Error(t('homecare.bookService.validation.patientInfoRequired')),
          );
        }

        try {
          uploads.reset();
          const fileIds = await Promise.all(
            (value.files ?? []).map((file) =>
              uploadFile(file, { onProgress: uploads.track(file) }),
            ),
          );

          const selectedService = vendor.services.find(
            (s) => s.id === value.vendorServiceId,
          );

          const vendorName =
            vendor.vendorType === 'INDIVIDUAL'
              ? `${vendor.individualProfile?.firstName} ${vendor.individualProfile?.lastName}`
              : vendor.clinicProfile?.clinicName;

          homecareReservationStore.write({
            description: value.symptoms,
            serviceId: selectedService?.serviceId ?? '',
            vendorServiceId: selectedService?.id ?? '',
            vendorId: vendor.id,
            serviceName: selectedService?.name ?? '',
            servicePrice: selectedService?.price ?? '',
            vendorName: vendorName ?? '',
            files: fileIds,
            scheduledDate: `${format(selectedSlot.datetime, 'yyyy-MM-dd')}T00:00:00.000Z`,
            scheduledMinutes: selectedSlot.startTime,
            timezone,
            ...(isOnBehalf && {
              patientName,
              patientBirthDate: new Date(value.patientBirthDate!).toISOString(),
              patientGender: value.patientGender,
            }),
          });

          navigate('/homecare/booking-confirmation');
          return void 0;
        } catch (error) {
          log.error({ action: 'homecare_book', error });
          return errorToFormIssue(error);
        }
      },
    },
  });

  // Auto-select if only one service
  const services = vendor?.services ?? [];
  if (
    services.length === 1 &&
    form.getFieldValue('vendorServiceId') !== services[0].id
  ) {
    form.setFieldValue('vendorServiceId', services[0].id);
  }

  const selectedService = services.find(
    (s) => s.id === form.getFieldValue('vendorServiceId'),
  );

  if (isLoading) return <Loading />;

  if (!vendor) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-gray-500">
          {t('homecare.bookService.vendorNotFound')}
        </p>
      </div>
    );
  }

  const vendorName =
    vendor.vendorType === 'INDIVIDUAL'
      ? `${vendor.individualProfile?.firstName} ${vendor.individualProfile?.lastName}`
      : vendor.clinicProfile?.clinicName;

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-green-600 p-2">
              <Home className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-base font-bold text-gray-900 md:text-2xl">
              {vendorName}
            </h1>
          </div>
        }
      >
        <ProfileCard />
      </Header>

      <div className="card-container">
        <Card className="card">
          <CardHeader className="text-center">
            <CardTitle className="text-xl font-bold">
              {t('homecare.bookService.title')}
            </CardTitle>
            <p className="text-sm text-gray-500">
              {t('homecare.bookService.subtitle')}
            </p>
          </CardHeader>

          <CardContent>
            <form
              className="space-y-8"
              onSubmit={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await form.handleSubmit();
              }}
            >
              {/* Section 1: Select Service */}
              {services.length > 1 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900">
                    {t('homecare.selectService')}
                  </h3>
                  <form.Field
                    name="vendorServiceId"
                    children={(field) => (
                      <div>
                        <RadioGroup
                          value={field.state.value}
                          onValueChange={(value) => field.handleChange(value)}
                          className="space-y-2"
                        >
                          {services.map((service) => (
                            <label
                              key={service.id}
                              className={cn(
                                'flex cursor-pointer items-center justify-between rounded-lg border p-4 transition-colors',
                                field.state.value === service.id
                                  ? 'border-primary bg-primary/5'
                                  : 'border-gray-200 hover:border-gray-300',
                              )}
                            >
                              <div className="flex items-center gap-3">
                                <RadioGroupItem value={service.id} />
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {service.name}
                                  </p>
                                  {service.description && (
                                    <p className="text-sm text-gray-500">
                                      {service.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <span className="font-bold text-green-600">
                                {parseFloat(service.price).toFixed(0)}{' '}
                                {t('payments.currencySar')}
                              </span>
                            </label>
                          ))}
                        </RadioGroup>
                        <FieldMessage errors={field.state.meta.errors} />
                      </div>
                    )}
                  />
                </div>
              )}

              {/* Section 2: Describe Your Needs */}
              <div className="space-y-4">
                <div className="space-y-1">
                  <h3 className="text-lg font-bold text-gray-900">
                    {t('homecare.bookService.describeNeeds')}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {t('homecare.bookService.needsSubtitle')}
                  </p>
                </div>

                {!isB2B && (
                  <RadioGroup
                    value={bookingFor}
                    onValueChange={(v) => {
                      if (v === 'self' || v === 'other') setBookingFor(v);
                    }}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="self" id="hc-self" />
                      <Label htmlFor="hc-self" className="cursor-pointer">
                        {t('booking.symptomChecker.bookingForSelf')}
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="other" id="hc-other" />
                      <Label htmlFor="hc-other" className="cursor-pointer">
                        {t('booking.symptomChecker.bookingForOther')}
                      </Label>
                    </div>
                  </RadioGroup>
                )}

                {isOnBehalf ? (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="grid w-full items-center gap-2">
                      <Label className="text-sm">
                        {t('booking.symptomChecker.fields.patientName')}
                      </Label>
                      <Input
                        placeholder={t(
                          'booking.symptomChecker.fields.patientNamePlaceholder',
                        )}
                        value={patientName}
                        onChange={(e) => setPatientName(e.target.value)}
                      />
                    </div>
                    <form.Field
                      name="patientBirthDate"
                      children={(field) => (
                        <BirthDateField field={field} star />
                      )}
                    />
                    <form.Field
                      name="patientGender"
                      children={(field) => (
                        <GenderField
                          field={field}
                          label={t('common.gender')}
                          placeholder={t('common.select')}
                          maleLabel={t('common.male')}
                          femaleLabel={t('common.female')}
                        />
                      )}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm text-gray-500">
                        {t('common.name')}
                      </Label>
                      <Input
                        value={fullName}
                        readOnly
                        className="mt-1 bg-gray-100"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">
                        {t('common.age')}
                      </Label>
                      <Input
                        value={age}
                        readOnly
                        className="mt-1 bg-gray-100"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">
                        {t('common.gender')}
                      </Label>
                      <Input
                        value={gender}
                        readOnly
                        className="mt-1 bg-gray-100"
                      />
                    </div>
                  </div>
                )}

                <form.Field
                  name="symptoms"
                  children={(field) => (
                    <div className="space-y-2">
                      <Label htmlFor="symptoms">
                        {t('homecare.bookService.describeNeeds')}
                      </Label>
                      <SymptomHints
                        hints={[
                          t('homecare.bookService.hints.service'),
                          t('homecare.bookService.hints.chronic'),
                          t('homecare.bookService.hints.duration'),
                        ]}
                      />
                      <Textarea
                        id="symptoms"
                        placeholder={t(
                          'homecare.bookService.symptomsPlaceholder',
                        )}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        className="min-h-[100px] resize-none"
                      />
                      <FieldMessage errors={field.state.meta.errors} />
                    </div>
                  )}
                />

                <form.Field
                  name="files"
                  children={(field) => (
                    <div className="space-y-2">
                      <AttachFilesField
                        field={field}
                        progress={uploads.progress}
                      />
                      <p className="text-xs text-gray-500">
                        {t('symptomForm.attachmentHint')}
                      </p>
                    </div>
                  )}
                />
              </div>

              {/* Section 3: Choose Date & Time */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-900">
                  {t('homecare.bookService.selectAppointment')}
                </h3>
                <div className="flex flex-col gap-4 md:flex-row">
                  <div className="flex-1">
                    <p className="mb-3 text-sm font-medium text-gray-600">
                      {t('homecare.selectDate')}
                    </p>
                    <Calendar
                      mode="single"
                      classNames={{ root: 'w-full' }}
                      selected={date}
                      onSelect={(value) => {
                        setDate(value);
                        setSelectedSlot(undefined);
                      }}
                      month={displayedMonth}
                      onMonthChange={setDisplayedMonth}
                      disabled={
                        isFetching
                          ? () => true
                          : [{ before: earliestBookableDate }, ...disabledDates]
                      }
                      showOutsideDays={false}
                      className="rounded-lg border bg-transparent p-3"
                      locale={i18n.language === 'ar' ? ar : enUS}
                    />
                  </div>
                  {date && (
                    <div className="flex-1">
                      <div className="border-t pt-4 md:border-t-0 md:border-r md:pt-0 md:pr-4">
                        <p className="mb-3 text-sm font-medium text-gray-600">
                          {t('homecare.selectTime')}
                        </p>
                        <TimeSlots
                          slots={selectedDateSlots}
                          date={date}
                          timezone={timezone}
                          isPending={isFetching}
                          selectedSlot={selectedSlot}
                          onSelectSlot={setSelectedSlot}
                          className="md:grid-cols-2"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <form.Subscribe
                selector={(state) => state.errors}
                children={(errors) => <FormMessage errors={errors} />}
              />
            </form>
          </CardContent>

          <CardFooter className="flex flex-col gap-4 border-t pt-6">
            {/* Price Summary */}
            {selectedService && (
              <div className="flex w-full items-center justify-between">
                <span className="text-gray-600">
                  {t('homecare.bookService.total')}
                </span>
                <span className="text-2xl font-bold text-green-600">
                  {parseFloat(selectedService.price).toFixed(0)}{' '}
                  {t('payments.currencySar')}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <PrivacyNote text={t('symptomForm.privacyNote')} />
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <div className="flex w-full justify-center gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(-1)}
                    className="secondary-button"
                  >
                    {t('homecare.back')}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => form.handleSubmit()}
                    disabled={!canSubmit || isSubmitting || !selectedSlot}
                    className="primary-button"
                  >
                    {isSubmitting
                      ? t('homecare.bookService.booking')
                      : t('homecare.confirmation.confirm')}
                  </Button>
                </div>
              )}
            />
          </CardFooter>
        </Card>
      </div>
    </>
  );
}

export async function homecareBookServiceLoader() {
  const identity = await requiresAuth();
  return { identity };
}
